/* ==========================================================================
   Fenou mobile navigation v2 — level switching
   --------------------------------------------------------------------------
   Vanilla custom element, no dependencies. Loaded from sections/header.liquid
   only while the "Mobile Navigation v2" setting is on.

   What this file is deliberately NOT responsible for:
     - opening/closing the drawer          -> <mobile-navigation> (theme.js)
     - trapping focus, ESC, overlay click  -> OpenableElement (theme.js)
   focus-trap recomputes its tabbable list on every Tab press, so hiding the
   inactive panels with the [hidden] attribute is enough to keep the trap
   pointed at whatever level is currently on screen.
   ========================================================================== */

(function () {
  'use strict';

  var SLIDE_MS = 260;

  /* Deliberately not requestAnimationFrame: the browser suspends rAF entirely
     while a tab is not being painted (background tab, occluded window, remote
     screenshot capture). A queued callback would then never run, silently
     dropping the focus move and the screen reader announcement. A 0ms timer is
     throttled in those cases but still fires. */
  function nextTick(fn) {
    setTimeout(fn, 0);
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var FenouNavPanels = class extends HTMLElement {
    connectedCallback() {
      this.rootPanel = this.querySelector('.fenou-nav__panel--root');
      this.statusNode = this.querySelector('[data-fenou-nav-status]');
      this.activePanel = null;

      this._onClick = this._onClick.bind(this);
      this._onDrawerClose = this._onDrawerClose.bind(this);

      this.addEventListener('click', this._onClick);

      // mobile-nav:close is dispatched by MobileNavigation (theme.js) whenever
      // the drawer is dismissed, no matter how: overlay, ESC or close button.
      this.drawer = this.closest('mobile-navigation');
      if (this.drawer) {
        this.drawer.addEventListener('mobile-nav:close', this._onDrawerClose);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this.drawer) {
        this.drawer.removeEventListener('mobile-nav:close', this._onDrawerClose);
      }
      clearTimeout(this.hideTimeout);
    }

    _onClick(event) {
      var opener = event.target.closest('[data-nav-open]');
      if (opener && this.contains(opener)) {
        event.preventDefault();
        this.openPanel(opener.getAttribute('data-nav-open'), opener);
        return;
      }

      var back = event.target.closest('[data-nav-back]');
      if (back && this.contains(back)) {
        event.preventDefault();
        this.closePanel();
      }
    }

    /* Reopening the drawer should always start at level 1. The reset runs
       after the drawer has slid out so the level change is not visible. */
    _onDrawerClose() {
      var delay = prefersReducedMotion() ? 0 : SLIDE_MS;
      clearTimeout(this.resetTimeout);
      this.resetTimeout = setTimeout(this.reset.bind(this), delay);
    }

    openPanel(panelId, opener) {
      var panel = panelId && this.querySelector('#' + CSS.escape(panelId));
      if (!panel || panel === this.activePanel) {
        return;
      }

      clearTimeout(this.hideTimeout);

      panel.hidden = false;
      // Force a reflow so the browser registers the off-canvas start position
      // before .is-active flips the transform, otherwise there is no transition.
      panel.offsetHeight;
      panel.classList.add('is-active');

      if (this.rootPanel) {
        this.rootPanel.classList.add('is-behind');
      }

      if (opener) {
        opener.setAttribute('aria-expanded', 'true');
        this.lastOpener = opener;
      }

      this.activePanel = panel;
      this.setAttribute('data-active-panel', panelId);

      this._announce(panel.getAttribute('data-panel-title'), true);
      this._focusFirst(panel);
    }

    closePanel() {
      var panel = this.activePanel;
      if (!panel) {
        return;
      }

      panel.classList.remove('is-active');
      if (this.rootPanel) {
        this.rootPanel.classList.remove('is-behind');
      }

      // Keep the panel in the DOM until it has slid out, then take it back out
      // of the tab order.
      var delay = prefersReducedMotion() ? 0 : SLIDE_MS;
      clearTimeout(this.hideTimeout);
      this.hideTimeout = setTimeout(function () {
        panel.hidden = true;
      }, delay);

      this.activePanel = null;
      this.setAttribute('data-active-panel', this.rootPanel ? this.rootPanel.id : '');

      this._announce(null, false);

      if (this.lastOpener) {
        this.lastOpener.setAttribute('aria-expanded', 'false');
        this.lastOpener.focus({ preventScroll: true });
        this.lastOpener = null;
      } else {
        this._focusFirst(this.rootPanel);
      }
    }

    /* Hard reset without animation, used when the drawer is closed. */
    reset() {
      var self = this;
      clearTimeout(this.hideTimeout);

      Array.prototype.forEach.call(this.querySelectorAll('.fenou-nav__panel--child'), function (panel) {
        panel.classList.remove('is-active');
        panel.hidden = true;
      });

      Array.prototype.forEach.call(this.querySelectorAll('[data-nav-open]'), function (opener) {
        opener.setAttribute('aria-expanded', 'false');
      });

      if (this.rootPanel) {
        this.rootPanel.classList.remove('is-behind');
        this.setAttribute('data-active-panel', this.rootPanel.id);
      }

      this.activePanel = null;
      this.lastOpener = null;

      if (self.statusNode) {
        self.statusNode.textContent = '';
      }
    }

    /* Belt and braces for browsers that ignore preventScroll or do not support
       overflow: clip -- if anything scrolled the panel stack sideways, put it
       back. Costs one property read per level change. */
    _resetSideScroll() {
      if (this.scrollLeft !== 0) {
        this.scrollLeft = 0;
      }
    }

    _focusFirst(panel) {
      if (!panel) {
        return;
      }
      var target = panel.querySelector('[data-nav-back], a[href], button:not([disabled])');
      if (target) {
        // preventScroll is what actually keeps the drawer still. The earlier
        // nextTick only deferred the focus by a tick, so it still landed while
        // the panel was mid-slide and the browser scrolled sideways to reveal
        // it -- which reads as the level change jumping.
        var self = this;
        nextTick(function () {
          target.focus({ preventScroll: true });
          self._resetSideScroll();
        });
      }
    }

    _announce(title, isLevelTwo) {
      if (!this.statusNode) {
        return;
      }

      var template = this.statusNode.getAttribute(isLevelTwo ? 'data-msg-level-two' : 'data-msg-level-one') || '';
      var message = isLevelTwo ? template.replace('%%TITLE%%', title || '') : template;

      // Re-setting the same string would not be re-announced by screen readers.
      this.statusNode.textContent = '';
      var node = this.statusNode;
      nextTick(function () {
        node.textContent = message;
      });
    }
  };

  if (!window.customElements.get('fenou-nav-panels')) {
    window.customElements.define('fenou-nav-panels', FenouNavPanels);
  }

  /* ========================================================================
     Hide the header on scroll down, bring it back on scroll up
     ------------------------------------------------------------------------
     Timing taken from rhodeskin.com: transform 0.7s cubic-bezier(.76,0,.24,1).
     The 30px accumulated threshold is the value the old category bar in this
     theme used (sections/fenou-catbar.liquid) -- it stops the bar flickering
     on small trackpad jitter.

     The class goes on the sticky section wrapper, not on <store-header>: the
     section is the element Focal makes position: sticky, so translating it
     moves the whole stuck box.
     ======================================================================== */

  var THRESHOLD = 30;
  var HIDDEN_CLASS = 'fenou-header--hidden';

  function initHideOnScroll() {
    var marker = document.querySelector('.header--hide-on-scroll');
    if (!marker) {
      return;
    }

    var section = marker.closest('.shopify-section--header') || marker.parentElement;
    if (!section) {
      return;
    }

    var lastY = window.scrollY;
    var accum = 0;
    var hidden = false;
    var ticking = false;

    /* Reasons never to pull the header away from the user. */
    function blocked() {
      // A drawer or the mini cart is open (OpenableElement sets this).
      if (document.documentElement.classList.contains('lock-all')) {
        return true;
      }
      // A desktop dropdown is open.
      if (document.querySelector('.mega-menu:not([hidden]), .nav-dropdown:not([hidden])')) {
        return true;
      }
      // Someone is tabbing through the header.
      if (document.activeElement && section.contains(document.activeElement)) {
        return true;
      }
      return false;
    }

    function apply(next) {
      if (next === hidden) {
        return;
      }
      hidden = next;
      section.classList.toggle(HIDDEN_CLASS, hidden);
    }

    function onFrame() {
      ticking = false;

      var y = window.scrollY;
      var delta = y - lastY;
      lastY = y;

      var height = section.offsetHeight || 0;

      /* Near the top the header is still in normal flow -- hiding it there
         would drag it over the announcement bar. */
      if (y <= height + 40) {
        accum = 0;
        apply(false);
        return;
      }

      if (blocked()) {
        apply(false);
        return;
      }

      /* Reset the accumulator whenever the direction changes, so the
         threshold measures one continuous movement. */
      if ((delta > 0 && accum < 0) || (delta < 0 && accum > 0)) {
        accum = 0;
      }
      accum += delta;

      if (accum > THRESHOLD) {
        accum = 0;
        apply(true);
      } else if (accum < -THRESHOLD) {
        accum = 0;
        apply(false);
      }
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(onFrame);
      }
    }, { passive: true });

    /* Reopening the drawer or closing a dropdown should not leave the header
       stranded off-screen. */
    document.addEventListener('mobile-nav:open', function () { apply(false); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHideOnScroll);
  } else {
    initHideOnScroll();
  }
})();
